import { useCallback, useMemo, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { OrderUseCases } from '../../core/domain/usecases/OrderUseCases';
import { OrderRepository } from '../../infrastructure/repositories/OrderRepository';
import { ApiService } from '../../infrastructure/services/ApiService';
import { User } from '../../core/domain/entities/User';

export const useOrders = () => {
  const { state, dispatch } = useApp();
  
  const apiService = useMemo(() => new ApiService(), []);
  const orderRepository = useMemo(() => new OrderRepository(apiService), [apiService]);
  const orderUseCases = useMemo(() => new OrderUseCases(orderRepository), [orderRepository]);

  const loadOrders = useCallback(async (user: User) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const orders = await orderUseCases.getOrdersForUser(user);
      dispatch({ type: 'SET_ORDERS', payload: orders });
    } catch (error) {
      console.error('Error loading orders:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Siparişler yüklenirken hata oluştu' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, orderUseCases]);

  // Delete order (soft delete) - using regular function to avoid circular dependency
  const deleteOrder = async (orderId: string, user: User): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      console.log('🗑️ Deleting order:', orderId);
      
      // Call API to delete order
      const result = await apiService.deleteOrder(orderId);
      
      if (result.success) {
        // Remove order from state (soft delete - status becomes "deleted")
        const currentOrders = state.orders;
        const orderIndex = currentOrders.findIndex(order => order.id === orderId);
        
        if (orderIndex !== -1) {
          // Update order status to "deleted" instead of removing completely
          const deletedOrder = { 
            ...currentOrders[orderIndex], 
            status: 'deleted' as const,
            updated_at: new Date().toISOString()
          };
          dispatch({ type: 'UPDATE_ORDER', payload: deletedOrder });
        }
        
        console.log('✅ Order deleted successfully:', orderId);
        return { success: true, message: 'Sipariş başarıyla silindi' };
      } else {
        throw new Error(result.error || 'Sipariş silinirken hata oluştu');
      }
      
    } catch (error) {
      console.error('❌ Error deleting order:', error);
      const errorMessage = error instanceof Error ? error.message : 'Sipariş silinirken hata oluştu';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleOrderAction = useCallback(async (orderId: string, action: string, user: User) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Optimistic update - immediately update the order status in state
      const currentOrders = state.orders;
      const orderIndex = currentOrders.findIndex(order => order.id === orderId);
      
      if (orderIndex !== -1) {
        const currentOrder = currentOrders[orderIndex];
        let newStatus = currentOrder.status;
        
        // Determine new status based on action
        switch (action) {
          case 'approve':
            newStatus = 'approved';
            break;
          case 'cancel':
            newStatus = 'cancelled';
            break;
          case 'prepare':
            newStatus = 'preparing';
            break;
          case 'ready':
            newStatus = 'ready_for_shipping';
            break;
          case 'ship':
            newStatus = 'in_transit';
            break;
          case 'deliver':
            newStatus = 'delivered';
            break;
          default:
            newStatus = currentOrder.status;
        }
        
        // Optimistic update with proper order data
        const optimisticOrder = { 
          ...currentOrder, 
          status: newStatus,
          updated_at: new Date().toISOString()
        };
        dispatch({ type: 'UPDATE_ORDER', payload: optimisticOrder });
      }
      
      let updatedOrder: any;
      
      switch (action) {
        case 'approve':
          updatedOrder = await orderUseCases.approveOrder(orderId, user);
          break;
        case 'prepare':
          updatedOrder = await orderUseCases.prepareOrder(orderId, user);
          break;
        case 'ready':
          updatedOrder = await orderUseCases.shipOrder(orderId, user);
          break;
        case 'ship':
          // This would be handled by warehouse manager
          updatedOrder = await orderRepository.updateStatus(orderId, 'in_transit');
          break;
        case 'deliver':
          updatedOrder = await orderUseCases.deliverOrder(orderId, user);
          break;
        case 'cancel':
          try {
            updatedOrder = await orderUseCases.cancelOrder(orderId, user);
          } catch (cancelError) {
            console.error('Cancel order error:', cancelError);
            throw cancelError;
          }
          break;
        case 'delete':
          try {
            const deleteResult = await deleteOrder(orderId, user);
            if (deleteResult.success) {
              return {
                success: true,
                action: 'delete',
                newStatus: 'deleted',
                orderId
              };
            } else {
              throw new Error(deleteResult.error || 'Sipariş silinirken hata oluştu');
            }
          } catch (deleteError) {
            console.error('Delete order error:', deleteError);
            throw deleteError;
          }
        default:
          throw new Error('Invalid action');
      }
      
      // Update the order in state with the actual response from backend
      if (updatedOrder) {
        // Ensure we have all required fields
        const completeUpdatedOrder = {
          ...updatedOrder,
          id: updatedOrder.id || orderId,
          status: updatedOrder.status || 'unknown',
          updated_at: updatedOrder.updated_at || new Date().toISOString()
        };
        
        dispatch({ type: 'UPDATE_ORDER', payload: completeUpdatedOrder });
      }
      
      // Return action result for parent component to handle tab switching
      return {
        success: true,
        action,
        newStatus: action === 'cancel' ? 'cancelled' : (updatedOrder?.status || 'unknown'),
        orderId
      };
      
    } catch (error) {
      console.error('Error handling order action:', error);
      const errorMessage = error instanceof Error ? error.message : 'Sipariş işlemi başarısız oldu';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      
      // Revert optimistic update on error
      if (action === 'cancel') {
        // Reload orders to get correct state
        try {
          const orders = await orderUseCases.getOrdersForUser(user);
          dispatch({ type: 'SET_ORDERS', payload: orders });
        } catch (reloadError) {
          console.error('Failed to reload orders after error:', reloadError);
        }
      }
      
      return {
        success: false,
        action,
        error: errorMessage
      };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, orderUseCases, orderRepository, state.orders]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredOrders = useCallback(() => {
    if (!state.user) return [];
    
    let orders = Array.isArray(state.orders) ? state.orders : [];
    
    // Filter by user role
    switch (state.user.role) {
      case 'salesperson':
        // Sales reps see their own orders - check both field names for compatibility
        orders = orders.filter(order => 
          order.salesRepresentativeId === state.user?.id || 
          order.salesperson_id === state.user?.id
        );
        break;
      case 'manager':
        // Managers see all orders
        break;
      case 'warehouse':
        // Warehouse managers see only approved orders
        orders = orders.filter(order => 
          order.status === 'approved' || order.status === 'preparing' || order.status === 'ready_for_shipping'
        );
        break;
      case 'admin':
        // Admin sees everything
        break;
      default:
        orders = [];
    }
    
    // Always filter out deleted orders for all users
    orders = orders.filter(order => order.status !== 'deleted');
    
    return orders;
  }, [state.orders, state.user]);

  // Return filtered orders directly from state
  const filteredOrders = getFilteredOrders();

  // Auto-reload orders when user changes
  useEffect(() => {
          if (state.user) {
        loadOrders(state.user);
      }
  }, [state.user, loadOrders]);

  return {
    orders: state.orders,
    filteredOrders: filteredOrders,
    loading: state.loading,
    error: state.error,
    loadOrders,
    handleOrderAction,
    deleteOrder,
  };
}; 